import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import BottomNav from '../components/BottomNav';
import useMobile from '../hooks/useMobile';
import API from '../config';
import { Helmet } from 'react-helmet-async';

const Profile = () => {
  const { token, logout, user } = useAuth();
  const { T } = useTheme();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [profile, setProfile]         = useState(null);
  const [stats, setStats]             = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [bets, setBets]               = useState([]);
  const [requests, setRequests]       = useState([]);
  const [activeTab, setActiveTab]     = useState('bets');
  const [betFilter, setBetFilter]     = useState('all');
  const [loading, setLoading]         = useState(true);
  const [newRequest, setNewRequest]   = useState({ title:'', description:'', category:'' });
  const [reqMessage, setReqMessage]   = useState('');
  const [reqLoading, setReqLoading]   = useState(false);
  const [deleteStep, setDeleteStep]   = useState(0);
  const [deleteError, setDeleteError] = useState('');
  const [rgSettings, setRgSettings]   = useState(null);
  const [rgLimits, setRgLimits]       = useState({ daily:'', weekly:'', monthly:'' });
  const [rgMsg, setRgMsg]             = useState('');
  const [rgLoading, setRgLoading]     = useState(false);
  const [excludePeriod, setExcludePeriod] = useState('');
  const [excludeConfirm, setExcludeConfirm] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchRG = async () => {
    try {
      const r = await axios.get(`${API}/api/responsible-gambling`, { headers: { Authorization: `Bearer ${token}` } });
      setRgSettings(r.data);
      setRgLimits({ daily: r.data.daily_deposit_limit||'', weekly: r.data.weekly_deposit_limit||'', monthly: r.data.monthly_deposit_limit||'' });
    } catch (e) { console.error(e); }
  };

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [profileRes, statsRes, txRes, betsRes, reqRes] = await Promise.all([
        axios.get(`${API}/api/users/profile`, { headers }),
        axios.get(`${API}/api/users/stats`, { headers }),
        axios.get(`${API}/api/users/transactions`, { headers }),
        axios.get(`${API}/api/users/my`, { headers }),
        axios.get(`${API}/api/market-requests/mine`, { headers }),
      ]);
      setProfile(profileRes.data);
      setStats(statsRes.data);
      setTransactions(txRes.data);
      setBets(betsRes.data);
      setRequests(reqRes.data);
      fetchRG();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getPayout = (bet) => {
    if (bet.status !== 'active' || !bet.potential_payout) return null;
    const payout = parseFloat(bet.potential_payout || 0);
    const amount = parseFloat(bet.amount || 0);
    const profit = payout - amount;
    const pct = amount > 0 ? Math.round((profit / amount) * 100) : 0;
    return { payout, profit: parseFloat(profit.toFixed(2)), pct };
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setReqLoading(true); setReqMessage('');
    try {
      const res = await axios.post(`${API}/api/market-requests`, newRequest, { headers: { Authorization: `Bearer ${token}` } });
      setRequests(prev => [res.data, ...prev]);
      setNewRequest({ title:'', description:'', category:'' });
      setReqMessage('success'); setTimeout(() => setReqMessage(''), 4000);
    } catch (err) { setReqMessage('error'); setTimeout(() => setReqMessage(''), 4000); }
    finally { setReqLoading(false); }
  };

  const handleDeleteAccount = async () => {
    setDeleteStep(2); setDeleteError('');
    try {
      await axios.delete(`${API}/api/users/delete-account`, { headers: { Authorization: `Bearer ${token}` } });
      logout(); navigate('/');
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete account. Please try again.');
      setDeleteStep(1);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2);
  };

  const isCredit = (type) => ['deposit', 'payout', 'sell', 'admin_credit'].includes(type);
  const getTxColor = (type) => isCredit(type) ? T.yes : T.no;
  const getTxIcon = (type) => ({ deposit:'↓', withdrawal:'↑', bet:'→', trade:'→', payout:'★', platform_fee:'•', sell:'→', admin_credit:'↓', admin_debit:'↑' }[type] || '•');

  const winRate = stats?.total_bets > 0 ? Math.round((stats.won_bets / stats.total_bets) * 100) : 0;
  const formatDate = (d) => new Date(d).toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' });
  const formatKES = (n) => `KES ${parseFloat(n||0).toLocaleString(undefined, { maximumFractionDigits:0 })}`;
  const getBetStatusColor = (status) => status === 'won' ? T.yes : status === 'lost' ? T.no : T.accent;
  const username = user?.username || profile?.username;

  const inputStyle = {
    width:'100%', background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8,
    padding:'10px 14px', color:T.text, fontSize:13, outline:'none', boxSizing:'border-box',
    fontFamily:"'DM Sans', sans-serif", transition:'border-color 0.15s',
  };

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ color:T.textDim, fontSize:13, fontFamily:"'DM Mono',monospace" }}>Loading...</p>
      </div>
    );
  }

  const TABS = [['bets','Trades'],['transactions','Transactions'],['suggest','Suggest'],['rg','Safe Trading']];
  const filteredBets = bets.filter(b => betFilter === 'all' || b.status === betFilter);

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'DM Sans','Segoe UI',sans-serif", color:T.text, paddingBottom:isMobile?80:0 }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-thumb{background:${T.border};border-radius:2px;}
        input:focus,textarea:focus,select:focus{border-color:${T.accent}!important;outline:none;}
      `}</style>

      <nav style={{ background:`${T.bg}f0`, backdropFilter:'blur(16px)', borderBottom:`1px solid ${T.border}`, height:56, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', position:'sticky', top:0, zIndex:100 }}>
        <div onClick={() => navigate('/markets')} style={{ cursor:'pointer', display:'flex', alignItems:'center' }}>
          <svg width="100" height="32" viewBox="0 0 120 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#131416" stroke="#2a2b2f" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
            <text x="46" y="20" dominantBaseline="middle" fontFamily="'DM Sans',sans-serif" fontWeight="800" fontSize="22" fill={T.text} letterSpacing="-1">mino</text>
          </svg>
        </div>
        <button onClick={() => navigate(-1)} style={{ background:'transparent', border:`1px solid ${T.border}`, borderRadius:7, padding:'6px 14px', cursor:'pointer', color:T.textMuted, fontSize:13 }}>← Back</button>
      </nav>

      <div style={{ maxWidth:600, margin:'0 auto', padding:isMobile?'28px 20px 40px':'36px 24px 60px', animation:'fadeUp 0.3s ease' }}>

        {/* Profile header — single-tone avatar, no gradient mix */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ width:64, height:64, borderRadius:'50%', background:T.surfaceHi, border:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:700, margin:'0 auto 16px', color:T.text, letterSpacing:'-0.5px' }}>
            {getInitials(profile?.full_name)}
          </div>
          <h1 style={{ fontSize:20, fontWeight:700, letterSpacing:'-0.4px', marginBottom:4 }}>{profile?.full_name}</h1>
          {username && <p style={{ color:T.accent, fontSize:13, fontWeight:600, marginBottom:4, fontFamily:"'DM Mono',monospace" }}>@{username}</p>}
          <p style={{ color:T.textMuted, fontSize:13, marginBottom:2 }}>{profile?.phone || profile?.email}</p>
          <p style={{ color:T.textDim, fontSize:12 }}>Member since {formatDate(profile?.created_at)}</p>
        </div>

        {/* Stat strip — flat, single accent, no rainbow */}
        <div style={{ display:'flex', marginBottom:16, border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden' }}>
          {[
            { label:'Balance', value:formatKES(profile?.balance), color:T.yes },
            { label:'Trades', value:stats?.total_bets||0, color:T.text },
            { label:'Wins', value:stats?.won_bets||0, color:T.text },
            { label:'Returns', value:formatKES(stats?.total_winnings), color:T.text },
          ].map((s, i, arr) => (
            <div key={s.label} style={{ flex:1, textAlign:'center', padding:'14px 6px', borderLeft: i>0 ? `1px solid ${T.border}` : 'none' }}>
              <p style={{ fontSize:15, fontWeight:700, color:s.color, letterSpacing:'-0.4px', marginBottom:4, fontFamily:"'DM Mono',monospace", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.value}</p>
              <p style={{ color:T.textDim, fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Win rate */}
        <div style={{ marginBottom:28 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <p style={{ fontSize:11, fontWeight:700, color:T.textMuted, textTransform:'uppercase', letterSpacing:'0.06em' }}>Win Rate</p>
            <p style={{ color:winRate>=50?T.yes:T.no, fontWeight:700, fontSize:15, fontFamily:"'DM Mono',monospace" }}>{winRate}%</p>
          </div>
          <div style={{ height:3, background:T.surfaceHi, borderRadius:2, overflow:'hidden', marginBottom:8 }}>
            <div style={{ height:'100%', width:`${winRate}%`, background:winRate>=50?T.yes:T.no, borderRadius:2, transition:'width 0.8s ease' }}/>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            <span style={{ color:T.textDim, fontSize:11, fontFamily:"'DM Mono',monospace" }}>{stats?.won_bets||0} won</span>
            {stats?.total_bets > 0
              ? <span style={{ color:T.textDim, fontSize:11, fontFamily:"'DM Mono',monospace" }}>{bets.filter(b=>b.status==='lost').length} lost</span>
              : <span style={{ color:T.textDim, fontSize:12 }}>Place your first trade to start tracking</span>
            }
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:20 }}>
          {TABS.map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ flex:1, padding:'10px 8px', background:'transparent', border:'none', borderBottom:`2px solid ${activeTab===tab?T.accent:'transparent'}`, color:activeTab===tab?T.text:T.textMuted, fontWeight:activeTab===tab?600:400, cursor:'pointer', fontSize:13, whiteSpace:'nowrap', transition:'all 0.12s', marginBottom:-1, fontFamily:"'DM Sans',sans-serif" }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Bet History ── */}
        {activeTab === 'bets' && (
          <div>
            {bets.length > 0 && (
              <div style={{ display:'flex', gap:4, marginBottom:16 }}>
                {[['all','All'],['active','Active'],['won','Won'],['lost','Lost']].map(([val,label]) => (
                  <button key={val} onClick={() => setBetFilter(val)}
                    style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${betFilter===val?T.accent:T.border}`, background:betFilter===val?T.accentDim:'transparent', color:betFilter===val?T.accent:T.textMuted, fontWeight:betFilter===val?600:400, cursor:'pointer', fontSize:12, transition:'all 0.12s' }}>
                    {label} <span style={{ fontSize:10, color:betFilter===val?T.accent:T.textDim, fontFamily:"'DM Mono',monospace", marginLeft:3 }}>{val==='all'?bets.length:bets.filter(b=>b.status===val).length}</span>
                  </button>
                ))}
              </div>
            )}
            {filteredBets.length === 0 ? (
              <div style={{ textAlign:'center', color:T.textDim, padding:'40px 0', fontSize:14 }}>
                {bets.length === 0
                  ? <><p style={{ marginBottom:8 }}>No trades placed yet.</p><span style={{ color:T.accent, cursor:'pointer', fontSize:13 }} onClick={() => navigate('/markets')}>Browse markets →</span></>
                  : 'No trades in this category.'
                }
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column' }}>
                {filteredBets.map((bet, i) => {
                  const live = getPayout(bet);
                  return (
                    <div key={i} onClick={() => navigate(`/markets/${bet.market_id}`)}
                      style={{ padding:'14px 4px', cursor:'pointer', borderBottom: i < filteredBets.length - 1 ? `1px solid ${T.border}` : 'none', transition:'opacity 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity='0.7'}
                      onMouseLeave={e => e.currentTarget.style.opacity='1'}>

                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:0 }}>
                          <div style={{ width:32, height:32, borderRadius:7, overflow:'hidden', flexShrink:0, background:T.surfaceHi, border:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {bet.image_url
                              ? <img src={bet.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => e.target.style.display='none'}/>
                              : <span style={{ fontSize:9, fontWeight:800, color:T.textDim }}>{(bet.category||'MKT').slice(0,3).toUpperCase()}</span>
                            }
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ fontWeight:600, fontSize:13, color:T.text, marginBottom:3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{bet.title||`Market #${bet.market_id}`}</p>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <span style={{ fontSize:11, fontWeight:700, color:bet.prediction==='yes'?T.yes:bet.prediction==='no'?T.no:T.accent, fontFamily:"'DM Mono',monospace" }}>
                                {(bet.option_label||(bet.prediction==='yes'?(bet.yes_label||'YES'):bet.prediction==='no'?(bet.no_label||'NO'):bet.prediction))?.toUpperCase()}
                              </span>
                              <span style={{ color:T.textDim, fontSize:11 }}>{formatDate(bet.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <p style={{ fontWeight:700, fontSize:13, color:T.text, marginBottom:3, fontFamily:"'DM Mono',monospace" }}>{formatKES(bet.amount)}</p>
                          <span style={{ fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace", color:getBetStatusColor(bet.status) }}>
                            {bet.status==='won'?`+${formatKES(bet.payout)}`:bet.status?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      {live && (
                        <div style={{ marginTop:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <span style={{ fontSize:11, color:T.textDim }}>If win now (after 2% fee + 20% tax)</span>
                          <span style={{ fontSize:11, fontWeight:700, fontFamily:"'DM Mono',monospace", color:live.profit>=0?T.yes:T.no }}>
                            KES {live.payout.toFixed(2)} · {live.profit>=0?'+':''}KES {Math.abs(live.profit).toFixed(2)} · {live.pct}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Transactions ── */}
        {activeTab === 'transactions' && (
          <div style={{ display:'flex', flexDirection:'column' }}>
            {transactions.length === 0 ? (
              <div style={{ textAlign:'center', color:T.textDim, padding:'40px 0', fontSize:14 }}>No transactions yet.</div>
            ) : transactions.map((tx, i) => (
              <div key={i} style={{ padding:'12px 4px', display:'flex', alignItems:'center', gap:12, borderBottom: i < transactions.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                <span style={{ fontSize:14, color:getTxColor(tx.type), flexShrink:0, width:16, textAlign:'center' }}>
                  {getTxIcon(tx.type)}
                </span>
                <div style={{ flex:1 }}>
                  <p style={{ fontWeight:600, fontSize:13, color:T.text, marginBottom:2 }}>
                    {({ bet:'Trade', platform_fee:'Entry Fee', sell:'Position Sold', deposit:'Deposit', withdrawal:'Withdrawal', admin_credit:'Credit', admin_debit:'Debit', payout:'Payout' }[tx.type] || tx.type?.charAt(0).toUpperCase() + tx.type?.slice(1))}
                  </p>
                  <p style={{ color:T.textDim, fontSize:12 }}>{formatDate(tx.created_at)}</p>
                </div>
                <p style={{ fontWeight:700, fontSize:14, color:getTxColor(tx.type), fontFamily:"'DM Mono',monospace" }}>
                  {isCredit(tx.type)?'+':'-'}{formatKES(tx.amount)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* ── Suggest Market ── */}
        {activeTab === 'suggest' && (
          <div>
            <div style={{ marginBottom:20 }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:4 }}>Suggest a Market</h3>
              <p style={{ color:T.textMuted, fontSize:13, marginBottom:18, lineHeight:1.6 }}>Have a great prediction idea? Submit it and we'll review it.</p>
              {reqMessage==='success' && <div style={{ background:T.yesDim, border:`1px solid ${T.yes}33`, color:T.yes, padding:'10px 14px', borderRadius:8, marginBottom:14, fontSize:13, fontWeight:600 }}>Request submitted! We'll review it soon.</div>}
              {reqMessage==='error'   && <div style={{ background:T.noDim,  border:`1px solid ${T.no}33`,  color:T.no,  padding:'10px 14px', borderRadius:8, marginBottom:14, fontSize:13 }}>Failed to submit. Please try again.</div>}
              <form onSubmit={handleRequest} style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <input type="text" value={newRequest.title} onChange={e => setNewRequest({...newRequest,title:e.target.value})} placeholder="e.g. Will Kenya qualify for AFCON 2026?" required maxLength={200} style={inputStyle}/>
                <textarea value={newRequest.description} onChange={e => setNewRequest({...newRequest,description:e.target.value})} placeholder="Describe how this market should work and when it resolves..." rows={3} maxLength={2000} style={{ ...inputStyle, resize:'vertical', lineHeight:1.6 }}/>
                <div style={{ display:'flex', gap:10 }}>
                  <select value={newRequest.category} onChange={e => setNewRequest({...newRequest,category:e.target.value})} required style={{ ...inputStyle, cursor:'pointer', flex:1 }}>
                    <option value="">Select category</option>
                    {['politics','football','economics','entertainment','business','events','sports','technology'].map(c => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>
                    ))}
                  </select>
                  <button type="submit" disabled={reqLoading} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontWeight:700, cursor:reqLoading?'not-allowed':'pointer', fontSize:13, flexShrink:0, opacity:reqLoading?0.7:1 }}>
                    {reqLoading?'Sending...':'Submit'}
                  </button>
                </div>
              </form>
            </div>
            {requests.length > 0 && (
              <div>
                <p style={{ fontSize:11, color:T.textDim, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Your Requests</p>
                <div style={{ display:'flex', flexDirection:'column' }}>
                  {requests.map((req, i) => (
                    <div key={req.id} style={{ padding:'14px 4px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, borderBottom: i < requests.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                      <div style={{ flex:1 }}>
                        <p style={{ fontWeight:600, fontSize:13, color:T.text, marginBottom:4 }}>{req.title}</p>
                        <p style={{ color:T.textDim, fontSize:12, marginBottom:req.admin_note?4:0 }}>{req.category} · {formatDate(req.created_at)}</p>
                        {req.admin_note && <p style={{ color:T.accent, fontSize:12 }}>Admin: {req.admin_note}</p>}
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, flexShrink:0, color:req.status==='approved'?T.yes:req.status==='rejected'?T.no:T.accent }}>
                        {req.status.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Responsible Gambling ── */}
        {activeTab === 'rg' && (
          <div style={{ display:'flex', flexDirection:'column', gap:28 }}>
            <div>
              <h3 style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:4 }}>Deposit Limits</h3>
              <p style={{ color:T.textMuted, fontSize:13, marginBottom:18, lineHeight:1.6 }}>Set maximum deposit amounts. Lowering limits takes effect immediately; raising them requires 24 hours.</p>
              {rgMsg && <div style={{ background:rgMsg.includes('success')?T.yesDim:T.noDim, border:`1px solid ${rgMsg.includes('success')?T.yes:T.no}33`, color:rgMsg.includes('success')?T.yes:T.no, padding:'10px 14px', borderRadius:8, marginBottom:14, fontSize:13 }}>{rgMsg}</div>}
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[['daily','Daily Limit'],['weekly','Weekly Limit'],['monthly','Monthly Limit']].map(([key,label]) => (
                  <div key={key}>
                    <p style={{ color:T.textMuted, fontSize:12, fontWeight:600, marginBottom:6 }}>{label} (KES)</p>
                    <input type="number" min="0" value={rgLimits[key]} onChange={e => { const v=e.target.value; if(v===''||parseFloat(v)>=0) setRgLimits({...rgLimits,[key]:v}); }} placeholder="No limit" style={inputStyle}/>
                  </div>
                ))}
                <button disabled={rgLoading} onClick={async () => {
                  setRgLoading(true); setRgMsg('');
                  try {
                    await axios.put(`${API}/api/responsible-gambling/limits`, { daily_deposit_limit:rgLimits.daily||null, weekly_deposit_limit:rgLimits.weekly||null, monthly_deposit_limit:rgLimits.monthly||null }, { headers:{ Authorization:`Bearer ${token}` } });
                    setRgMsg('Limits updated successfully'); setTimeout(() => setRgMsg(''), 4000);
                  } catch (e) { setRgMsg(e.response?.data?.message||'Failed to update limits'); setTimeout(() => setRgMsg(''), 5000); }
                  finally { setRgLoading(false); }
                }} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:8, padding:11, fontWeight:700, cursor:rgLoading?'not-allowed':'pointer', fontSize:13, opacity:rgLoading?0.7:1, marginTop:4 }}>
                  {rgLoading?'Saving...':'Save Limits'}
                </button>
              </div>
            </div>

            <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:24 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:T.no, marginBottom:4 }}>Self-Exclusion</h3>
              <p style={{ color:T.textMuted, fontSize:13, marginBottom:18, lineHeight:1.6 }}>Temporarily or permanently exclude yourself from trading. This cannot be reversed during the exclusion period.</p>
              {rgSettings?.self_excluded && new Date(rgSettings.self_exclusion_ends_at) > new Date() ? (
                <div style={{ background:T.noDim, border:`1px solid ${T.no}33`, borderRadius:8, padding:'14px 16px' }}>
                  <p style={{ color:T.no, fontWeight:700, fontSize:13, marginBottom:4 }}>You are currently self-excluded</p>
                  <p style={{ color:T.textMuted, fontSize:13 }}>Exclusion ends: {new Date(rgSettings.self_exclusion_ends_at).toLocaleDateString('en-KE',{day:'numeric',month:'long',year:'numeric'})}</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <select value={excludePeriod} onChange={e => { setExcludePeriod(e.target.value); setExcludeConfirm(false); }} style={{ ...inputStyle, cursor:'pointer' }}>
                    <option value="">Select exclusion period</option>
                    <option value="24h">24 Hours</option>
                    <option value="7d">7 Days</option>
                    <option value="30d">30 Days</option>
                    <option value="permanent">Permanent</option>
                  </select>
                  {excludePeriod && !excludeConfirm && (
                    <button onClick={() => setExcludeConfirm(true)} style={{ background:'transparent', border:`1px solid ${T.no}44`, color:T.no, borderRadius:8, padding:11, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                      Self-Exclude for {excludePeriod==='24h'?'24 Hours':excludePeriod==='7d'?'7 Days':excludePeriod==='30d'?'30 Days':'Permanently'}
                    </button>
                  )}
                  {excludeConfirm && (
                    <div style={{ background:T.noDim, border:`1px solid ${T.no}33`, borderRadius:10, padding:16 }}>
                      <p style={{ color:T.no, fontWeight:700, fontSize:13, marginBottom:6 }}>Are you sure?</p>
                      <p style={{ color:T.textMuted, fontSize:13, marginBottom:14, lineHeight:1.6 }}>You will not be able to trade or deposit during this period.</p>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={async () => {
                          try { await axios.post(`${API}/api/responsible-gambling/self-exclude`, { period:excludePeriod }, { headers:{ Authorization:`Bearer ${token}` } }); fetchRG(); setExcludeConfirm(false); }
                          catch (e) { console.error(e); }
                        }} style={{ background:T.no, color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Confirm</button>
                        <button onClick={() => setExcludeConfirm(false)} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:8, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Logout */}
        <button onClick={async () => { await logout(); navigate('/'); }}
          style={{ width:'100%', marginTop:32, padding:13, background:'transparent', border:`1px solid ${T.border}`, color:T.textMuted, borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', transition:'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor=T.textMuted; e.currentTarget.style.color=T.text; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.color=T.textMuted; }}>
          Log Out
        </button>

        {/* Danger Zone */}
        <div style={{ marginTop:20, borderTop:`1px solid ${T.border}`, paddingTop:20 }}>
          <p style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:T.no, marginBottom:8 }}>Danger Zone</p>
          <p style={{ color:T.textMuted, fontSize:13, marginBottom:16, lineHeight:1.6 }}>Permanently delete your account and all associated data. Compliant with the Kenya Data Protection Act 2019.</p>
          {deleteError && <div style={{ background:T.noDim, border:`1px solid ${T.no}33`, color:T.no, padding:'10px 14px', borderRadius:8, marginBottom:12, fontSize:13 }}>{deleteError}</div>}
          {deleteStep === 0 && (
            <button onClick={() => setDeleteStep(1)} style={{ background:'transparent', border:`1px solid ${T.no}44`, color:T.no, borderRadius:8, padding:'9px 18px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Delete My Account</button>
          )}
          {deleteStep === 1 && (
            <div style={{ background:T.noDim, border:`1px solid ${T.no}33`, borderRadius:10, padding:16 }}>
              <p style={{ color:T.no, fontWeight:700, fontSize:13, marginBottom:6 }}>Are you absolutely sure?</p>
              <p style={{ color:T.textMuted, fontSize:13, marginBottom:14, lineHeight:1.6 }}>This will permanently delete your account, trades, transactions, and personal data. Withdraw your balance first.</p>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={handleDeleteAccount} style={{ background:T.no, color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontWeight:700, cursor:'pointer', fontSize:13 }}>Yes, Delete Everything</button>
                <button onClick={() => { setDeleteStep(0); setDeleteError(''); }} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:8, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
              </div>
            </div>
          )}
          {deleteStep === 2 && <p style={{ color:T.no, fontSize:13, fontWeight:600 }}>Deleting your account...</p>}
        </div>
      </div>
      <BottomNav/>
    </div>
  );
};

export default Profile;